import { useEffect, useRef, useState } from "react";
import { Button, ButtonProps } from "react-bootstrap";
import { ExclamationCircle } from "react-bootstrap-icons";
import { IRequestStatus, PauseCover } from "../utility";

export interface AsyncButtonProps extends ButtonProps {
  id: string;
  requestStatus?: IRequestStatus<unknown> | IRequestStatus<unknown>[];
  pauseCoverText?: string;
}

/**
 * Button component that displays loading state and error messages based on request status.
 *
 * @param props - The component props
 * @param props.id - Unique identifier for the button element
 * @param props.requestStatus - Request status object or array to display loading and error states
 * @param props.pauseCoverText - Optional text to display during loading state
 */
export const AsyncButton = ({ requestStatus, pauseCoverText, ...props }: AsyncButtonProps) => {
  const requesting = Array.isArray(requestStatus)
    ? requestStatus.some((s) => s.requesting)
    : requestStatus?.requesting;

  const error = Array.isArray(requestStatus)
    ? requestStatus.some((s) => s.error)
    : requestStatus?.error;

  const errorText = Array.isArray(requestStatus)
    ? requestStatus
        .filter((s) => s.error && s.errorText)
        ?.map((s) => s.errorText)
        .join(", ")
    : requestStatus?.errorText;

  const buttonRef = useRef<HTMLDivElement>(null);
  const coverTextRef = useRef<HTMLDivElement>(null);
  const [minWidth, setMinWidth] = useState("0px");

  useEffect(() => {
    const button = buttonRef.current;
    const coverText = coverTextRef.current;

    if (button && coverText && requesting) {
      let coverTextWidth = coverText.offsetWidth;
      coverTextWidth = coverTextWidth + 26; // 26 for the spinner and a bit of padding
      const buttonWidth = button.offsetWidth;

      /* istanbul ignore else */
      if (coverTextWidth > buttonWidth) {
        setMinWidth(`${coverTextWidth}px`);
      }
    } else {
      setMinWidth("0px");
    }
  }, [requesting]);

  return (
    <Button
      ref={buttonRef}
      role="button"
      {...props}
      disabled={props.disabled || requesting}
      onClick={!requesting ? props.onClick : undefined}
      title={error ? errorText : props.title}
      style={{
        ...props.style,
        minWidth,
        position: "relative",
      }}
    >
      {requesting ? (
        <>
          <PauseCover
            ref={coverTextRef}
            text={pauseCoverText}
          />
          {props.children}
        </>
      ) : error ? (
        <>
          <ExclamationCircle /> {errorText?.trim() !== "" ? errorText : "Error"}
        </>
      ) : (
        props.children
      )}
    </Button>
  );
};

AsyncButton.displayName = "AsyncButton";
